import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueue } from '../../api/use-queue';
import { devError, wait } from '../../../../utils/browser';

export function useInstallmentCache({ token, offer, enabled = false, updateOfferHash, onError }) {
  const [busy, setBusy] = useState(false);
  const [cache, setCache] = useState([]);
  const [lastHash, setLastHash] = useState('');

  const queueInstallments = useQueue({
    key: 'cache-regeneration',
    token,
    retryLimit: 30,
    timeout: 15000,
    enqueueEndpoint: 'energia-solar-queue/v2/simulation/renegerate-installment-cache',
    onError: (error) => {
      if (onError) onError(error);
      devError('useInstallmentCache', error);
      setTimeout(() => {
        setBusy(false);
      }, 200);
    },
    onComplete: (response) => {
      setCache(response?.cache || []);
      setLastHash(response?.hash);
      setBusy(false);
      updateOfferHash(response?.hash);
    },
  });

  const renegerate = useCallback(async () => {
    if (busy || !enabled) return;
    setBusy(true);

    try {
      await queueInstallments.sendToQueue();
      await wait(1000);
    } catch (error) {
      setBusy(false);
    }
  }, [busy, enabled, queueInstallments]);

  const installments = useMemo(() => {
    let base = [];

    if (cache && cache.length > 0) {
      base = cache;
    } else {
      base = offer?.installmentCache || [];
    }

    return base.filter((item) => item.hash === lastHash);
  }, [cache, lastHash, offer?.installmentCache]);

  const reset = useCallback(() => {
    setCache([]);
    setLastHash('');
    setBusy(false);
  }, []);

  useEffect(() => {
    if (!enabled || busy || !offer?.hash) return;

    if (offer?.hash !== lastHash) {
      renegerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, enabled, lastHash, offer?.hash]);

  return {
    renegerate,
    installments,
    reset,
  };
}
